import React, { type ReactNode, useContext } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { Dropdown, Popup } from "semantic-ui-react";

import type { OpenAPI } from "simplydo/interfaces";

import api from "api";
import util from "utils/utils";

import type { ILaneData } from "./Board";
import { ProjectBoardContext } from "./ProjectBoardContext";

export type ILaneSettings = {
  lane: ILaneData;
  editLaneName: () => void;
  editLaneDescription: () => void;
  updateLane: (
    data: Partial<OpenAPI.Schemas["ProjectLane"]>,
    onComplete?: () => void,
    onError?: () => void,
  ) => void;
  trigger: ReactNode;
  reorderLaneIdeas: (sort: string, sortDirection: string) => void;
};

export const DropdownLaneSettings = ({
  lane,
  editLaneName,
  editLaneDescription,
  updateLane,
  trigger,
  reorderLaneIdeas,
}: ILaneSettings) => {
  const { forId, projectWebsocket, lanes, checkUpdateProjectLane } =
    useContext(ProjectBoardContext);

  const { cards } = lane;

  const { t } = useTranslation();

  const currentLaneId = lane.id || "default";
  const isDefaultLane = currentLaneId === "default";
  const currentLaneMetaType = lane?.metaType || "normal";

  const LaneTypeOptions: {
    key: OpenAPI.Schemas["projectLanes"]["metaType"];
    value: OpenAPI.Schemas["projectLanes"]["metaType"];
    text: string;
  }[] = [
    { key: "normal", value: "normal", text: "Normal lane" },
    {
      key: "notProgressing",
      value: "notProgressing",
      text: `${t("common:capitalise", { key: "generic.ideas" })} not progressing`,
    },
    {
      key: "completed",
      value: "completed",
      text: `${t("common:capitalise", { key: "generic.ideas" })} completed`,
    },
    {
      key: "resolved",
      value: "resolved",
      text: `${t("common:capitalise", { key: "generic.ideas" })} resolved`,
    },
  ];

  const LaneSortOptions: { text: string; value: string }[] = [
    { value: "projectUpdated", text: "Last update" },
    { value: "projectDeadline", text: "Project deadline" },
    {
      value: "ideaCreated",
      text: `${t("common:capitalise", { key: "generic.idea" })} creation`,
    },
    { value: "alphabetical", text: "Card name" },
    { value: "assessmentScore", text: "Average assessment score" },
  ];

  const moveLane = (direction) => {
    if (direction === -1 && lane.order < 1) return;
    if (direction === 1 && lane.order >= lanes.length - 1) return;
    api.boards.updateProjectLane(
      forId,
      currentLaneId,
      { order: lane.order + direction },
      () => {
        api.boards.getProjectLanes(
          forId,
          () => {
            projectWebsocket?.current?.publish?.("lanesUpdated");
            api.invalidateQueries(api.boards.getProjectLanes, [forId]);
          },
          () => {},
        );
      },
      (err) => toast.error(err.message),
    );
  };

  const deleteLane = () => {
    util
      .confirm(
        "Really delete this lane?",
        `All ${t("generic.ideas")} on this lane will be moved back to the default lane.`,
      )
      .then(() => {
        api.boards.deleteProjectLane(
          forId,
          currentLaneId,
          () => {
            projectWebsocket?.current?.publish?.("lanesUpdated");
            projectWebsocket?.current?.publish?.("ideasUpdated");
            api.invalidateQueries(api.boards.getProjectLanes, [forId]);
            api.invalidateQueries(api.challenges.getIdeas, [forId]);
          },
          (err) => toast.error(err.message),
        );
      })
      .catch(() => {});
  };

  return (
    <Popup
      mountNode={document.getElementById("semantic-modal-mount-node")}
      on="click"
      trigger={trigger}
      style={{ padding: 0 }}
      content={
        <div className="ui dropdown">
          <Dropdown.Menu
            className="visible"
            style={{ position: "relative", border: "none", boxShadow: "none" }}
          >
            <Dropdown.Header content="Lane Settings" />
            <Dropdown.Divider />
            {lanes.length > 1 && !isDefaultLane ?
              <Dropdown item pointing="left" text="Move lane">
                <Dropdown.Menu>
                  {lane.order > 0 ?
                    <Dropdown.Item
                      content="Move left"
                      onClick={() => moveLane(-1)}
                    />
                  : null}
                  {lane.order < lanes.length - 1 ?
                    <Dropdown.Item
                      content="Move right"
                      onClick={() => moveLane(1)}
                    />
                  : null}
                </Dropdown.Menu>
              </Dropdown>
            : null}
            {cards?.length ?
              <Dropdown
                item
                pointing="left"
                upward={false}
                text={`Reorder ${t("generic.ideas")} by...`}
              >
                <Dropdown.Menu>
                  {LaneSortOptions.map((l, i) => (
                    <React.Fragment key={l.value}>
                      {i !== 0 ?
                        <Dropdown.Divider />
                      : null}
                      <Dropdown.Item
                        content={`${l.text} (descending)`}
                        onClick={() => reorderLaneIdeas(l.value, "desc")}
                      />
                      <Dropdown.Item
                        content={`${l.text} (ascending)`}
                        onClick={() => reorderLaneIdeas(l.value, "asc")}
                      />
                    </React.Fragment>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            : null}

            {!isDefaultLane ?
              <Popup
                trigger={
                  <Dropdown item pointing="left" text="Change lane type">
                    <Dropdown.Menu>
                      {LaneTypeOptions.filter(
                        (l) => l.value !== currentLaneMetaType,
                      ).map((l) => (
                        <Dropdown.Item
                          key={l.key}
                          content={l.text}
                          onClick={() => updateLane({ metaType: l.value })}
                        />
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                }
                content={`${t("common:capitalise", { key: "generic.ideas" })} in typed lanes do not send notifications to assignees when the ${t("generic.idea")} has not been updated in a while`}
              />
            : null}

            {cards?.length && lanes?.length > 0 ?
              <Dropdown
                item
                pointing="left"
                text={`Move all ${t("generic.ideas")} to other lane`}
              >
                <Dropdown.Menu>
                  {lanes
                    .filter((l) => l._id !== currentLaneId)
                    .map((l) => (
                      <Dropdown.Item
                        key={l._id}
                        content={l.name}
                        onClick={() =>
                          checkUpdateProjectLane(
                            cards.map((card) => card.idea._id),
                            l._id,
                            null,
                          )
                        }
                      />
                    ))}
                </Dropdown.Menu>
              </Dropdown>
            : null}
            {!isDefaultLane ?
              <Dropdown.Divider />
            : null}
            <Dropdown.Item
              content="Edit lane name"
              onClick={() => editLaneName()}
            />
            <Dropdown.Item
              content="Edit lane description"
              onClick={() => editLaneDescription()}
            />
            {!isDefaultLane ?
              <Dropdown.Item
                content="Delete lane"
                style={{ color: "#db2828" }}
                onClick={() => deleteLane()}
              />
            : null}
          </Dropdown.Menu>
        </div>
      }
    />
  );
};
